<template>
  <base-drawer
    class="pass-type-drawer"
    :show="show"
    :title="isEdit ? 'Edit pass type' : 'Create pass type'"
    width="480px"
    @close="onClose"
  >
    <drawer-section title="Details" icon="icon-notes">
      <base-input
        label="Name"
        placeholder="Summer pass"
        v-model="form.name"
      />
      <base-input
        label="Internal label"
        placeholder="Staff-only label (optional)"
        v-model="form.internal_label"
      />
      <base-input label="Description">
        <el-input
          type="textarea"
          :rows="2"
          placeholder="Shown to customers when the pass is sold online (optional)"
          v-model="form.description"
        />
      </base-input>
    </drawer-section>

    <drawer-section title="Validity and allowance" icon="icon-time-alarm">
      <info-hint>
        A pass is valid for the chosen duration from the moment it is granted or purchased. <strong>Per booking</strong> comps up to the allowance on every eligible booking during the window; <strong>total for the pass</strong> is a balance that depletes as tickets are comped.
      </info-hint>

      <label>Valid for</label>
      <div class="duration-row">
        <base-input class="duration-value mb-0">
          <el-input-number
            :controls="false"
            v-model="form.duration_value"
            :min="1"
            :precision="0"
            placeholder="3"
          />
        </base-input>
        <base-input class="duration-unit mb-0">
          <el-select v-model="form.duration_unit">
            <el-option
              v-for="unit in durationUnits"
              :key="unit.value"
              :label="form.duration_value === 1 ? unit.singular : unit.plural"
              :value="unit.value"
            />
          </el-select>
        </base-input>
      </div>

      <label class="mt-3">Allowance</label>
      <div class="mb-2">
        <segmented-toggle v-model="form.allowance_type" :options="allowanceOptions" size="sm" />
      </div>

      <base-input :label="form.allowance_type === 'per_booking' ? 'Tickets comped per booking' : 'Total tickets included'">
        <el-input-number
          :controls="false"
          v-model="form.ticket_allowance"
          :min="1"
          :precision="0"
          placeholder="4"
        />
      </base-input>
    </drawer-section>

    <drawer-section title="Games" icon="icon-controller">
      <div class="toggle-row">
        <el-switch active-color="#e14eca" v-model="form.applies_to_all_games" />
        <label class="mb-0 ml-2" style="cursor: pointer" @click="form.applies_to_all_games = !form.applies_to_all_games">
          Applies to all games
        </label>
      </div>

      <base-input v-if="!form.applies_to_all_games" label="Covered games" class="mt-2">
        <el-select
          multiple
          filterable
          v-model="form.linked_game_ids"
          placeholder="Select games"
        >
          <el-option
            v-for="game in availableGames"
            :key="game.id"
            :label="game.name"
            :value="game.id"
          />
        </el-select>
      </base-input>
    </drawer-section>

    <drawer-section title="Online sales" icon="icon-cart">
      <div class="toggle-row">
        <el-switch active-color="#e14eca" v-model="form.is_purchasable" />
        <label class="mb-0 ml-2" style="cursor: pointer" @click="form.is_purchasable = !form.is_purchasable">
          Sell this pass on the booking site
        </label>
      </div>

      <template v-if="form.is_purchasable">
        <label class="mt-2">Price</label>
        <div class="amount-input-group">
          <span class="amount-prefix">{{ currencySymbol }}</span>
          <base-input class="amount-input-wrapper mb-0">
            <el-input-number
              :controls="false"
              v-model="form.price"
              :min="0"
              :precision="2"
              placeholder="0.00"
            />
          </base-input>
        </div>
        <p class="purchase-tax-note">Pass sales are taxed like regular products at checkout</p>
      </template>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          <i v-if="saving" class="el-icon-loading mr-1"></i>{{ isEdit ? 'Save' : 'Create pass type' }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { getCurrencyInfo } from '@/util/currencyFormat'

const props = defineProps({
  show: { type: Boolean, default: false },
  passType: { type: Object, default: null }
})

const emit = defineEmits(['update:show', 'close', 'saved'])

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const currencySymbol = getCurrencyInfo().symbol

const durationUnits = [
  { value: 'day', singular: 'Day', plural: 'Days' },
  { value: 'week', singular: 'Week', plural: 'Weeks' },
  { value: 'month', singular: 'Month', plural: 'Months' },
  { value: 'year', singular: 'Year', plural: 'Years' }
]
const allowanceOptions = [
  { value: 'per_booking', label: 'Per booking' },
  { value: 'total', label: 'Total for the pass' }
]

const isEdit = computed(() => !!(props.passType && props.passType.id))

const saving = ref(false)
const availableGames = ref<any[]>([])
const gamesLoaded = ref(false)

function defaultForm() {
  return {
    name: '',
    internal_label: '',
    description: '',
    duration_value: 1,
    duration_unit: 'month',
    allowance_type: 'per_booking',
    ticket_allowance: 1,
    applies_to_all_games: true,
    linked_game_ids: [] as number[],
    is_purchasable: false,
    price: undefined as number | undefined
  }
}

const form = ref(defaultForm())

function loadForm() {
  if (props.passType && props.passType.id) {
    const pt = props.passType
    form.value = {
      name: pt.name || '',
      internal_label: pt.internal_label || '',
      description: pt.description || '',
      duration_value: pt.duration_value,
      duration_unit: pt.duration_unit,
      allowance_type: pt.allowance_type,
      ticket_allowance: pt.ticket_allowance,
      applies_to_all_games: !!pt.applies_to_all_games,
      linked_game_ids: (pt.games || []).map((g: any) => g.id),
      is_purchasable: !!pt.is_purchasable,
      price: pt.price != null ? Number(pt.price) : undefined
    }
  } else {
    form.value = defaultForm()
  }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function getGames() {
  if (gamesLoaded.value) return
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/games`, config)
    const games = Array.isArray(response.data) ? response.data : (response.data.games || [])
    availableGames.value = games
      .filter((g: any) => !g.archived)
      .map((g: any) => ({ id: g.id, name: g.internal_name || g.name }))
    gamesLoaded.value = true
  } catch (err) {
    console.log(err)
  }
}

function validate(): string | null {
  if (!form.value.name || !form.value.name.trim()) return 'Name is required'
  if (!form.value.duration_value || form.value.duration_value < 1) return 'Duration must be at least 1'
  if (!form.value.ticket_allowance || form.value.ticket_allowance < 1) return 'Ticket allowance must be at least 1'
  if (!form.value.applies_to_all_games && form.value.linked_game_ids.length === 0) {
    return 'Select at least one game or enable all games'
  }
  if (form.value.is_purchasable && (!form.value.price || form.value.price <= 0)) {
    return 'A price greater than zero is required to sell online'
  }
  return null
}

async function save() {
  const validationMessage = validate()
  if (validationMessage) {
    proxy.$notify({ type: 'warning', message: validationMessage, icon: 'tim-icons icon-alert-circle-exc' })
    return
  }

  saving.value = true
  try {
    const config = await authConfig()
    const payload = {
      name: form.value.name.trim(),
      internal_label: form.value.internal_label || null,
      description: form.value.description || null,
      duration_value: form.value.duration_value,
      duration_unit: form.value.duration_unit,
      allowance_type: form.value.allowance_type,
      ticket_allowance: form.value.ticket_allowance,
      applies_to_all_games: form.value.applies_to_all_games ? 1 : 0,
      linked_game_ids: form.value.applies_to_all_games ? [] : form.value.linked_game_ids,
      is_purchasable: form.value.is_purchasable ? 1 : 0,
      price: form.value.is_purchasable ? form.value.price : null
    }

    if (isEdit.value) {
      await axios.put(`${baseUrl}/pass-types/${props.passType.id}`, payload, config)
    } else {
      await axios.post(`${baseUrl}/pass-types`, payload, config)
    }

    proxy.$notify({
      type: 'success',
      message: isEdit.value ? 'Pass type saved' : 'Pass type created',
      icon: 'tim-icons icon-check-2'
    })
    emit('saved')
    emit('update:show', false)
    emit('close')
  } catch (err: any) {
    // New lambdas return JSON errors; surface field validation when present
    let message = 'The pass type could not be saved'
    const data = err?.response?.data
    if (data && Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
      message = data.validation_errors[0].message
    } else if (data && data.error) {
      message = data.error
    }
    swal('Error!', message, 'error')
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
    getGames()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
  }
}

.duration-row {
  display: flex;
  gap: 10px;

  .duration-value {
    flex: 0 0 110px;
  }
  .duration-unit {
    flex: 1;
  }
}

.purchase-tax-note {
  margin: 6px 0 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}

.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  &:focus-within { border-color: #e14eca; }
}
.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}
.amount-input-wrapper {
  flex: 1;
  ::v-deep .form-control { border: none; border-radius: 0; background: transparent; }
  ::v-deep .el-input-number { width: 100%; }
  ::v-deep .el-input-number .el-input__inner { text-align: left; color: white; background: transparent; border: none; }
}
</style>

<style lang="scss">
.white-content .pass-type-drawer {
  .toggle-row label {
    color: #525f7f;
  }
  .purchase-tax-note {
    color: rgba(0, 0, 0, 0.5);
  }
  .amount-input-group {
    background: rgba(255, 255, 255, 0.8);
  }
  .amount-input-wrapper .el-input-number .el-input__inner {
    color: #222a42;
  }
}
</style>
